// src/notifications/services/use-unseen-notification-count.ts
// GET /api/Notifications/GetUserUnSeenNotificationCount — the bell badge count.
//
// Mirrors Flutter `getUnSeenNotificationsCount`
// (notification_center_data_source_impl.dart:47-54): the endpoint returns a bare
// integer as the response body. The shared `get` helper JSON-parses the body;
// `JSON.parse("5")` → 5, so a bare integer decodes cleanly. A missing/empty body
// falls back to 0.
//
// Polled on an interval so the badge stays live without a dedicated SignalR hub
// (the Flutter app refreshes the count on push + on app resume; a 60s poll is the
// web-shell equivalent and matches how other ambient counters refresh here).
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';

/** Prefix root for the unseen-count cache; scoped per-user via `unseenNotificationCountKey`. */
export const unseenNotificationCountQueryKey = ['notifications', 'unseen-count'] as const;

/**
 * Per-user unseen-count key. The QueryClient outlives a logout/login, so the
 * count (PHI-adjacent) must be keyed by the signed-in user, never global.
 */
export function unseenNotificationCountKey(userId: string | undefined) {
  return [...unseenNotificationCountQueryKey, userId ?? 'anonymous'] as const;
}

const POLL_INTERVAL_MS = 60_000;

export function useUnseenNotificationCount(params?: {
  enabled?: boolean;
}): UseQueryResult<number, Error> {
  const enabled = params?.enabled ?? true;
  const userId = useAuthStore((s) => s.user?.userId);
  return useQuery({
    queryKey: unseenNotificationCountKey(userId),
    enabled: enabled && userId != null,
    refetchInterval: POLL_INTERVAL_MS,
    queryFn: async ({ signal }) => {
      const value = await get<number | null>(
        '/api/Notifications/GetUserUnSeenNotificationCount',
        { signal },
      );
      return typeof value === 'number' && Number.isFinite(value) ? value : 0;
    },
  });
}
